import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';

const problems = [
  { icon: 'ri-phone-missed-line', title: 'Missed calls turn into lost jobs', desc: 'Every unanswered call is a potential client going straight to your competitor.', color: '#FF2B2B' },
  { icon: 'ri-time-line', title: 'Slow responses lose high-intent prospects', desc: 'High-intent buyers move fast. A delay of even minutes drops conversion dramatically.', color: '#FF2B2B' },
  { icon: 'ri-shuffle-line', title: 'Manual follow-up creates inconsistency', desc: 'Human-driven follow-up is unpredictable — leading to dropped leads and missed revenue.', color: '#FF2B2B' },
  { icon: 'ri-moon-line', title: 'Staff can\'t respond instantly 24/7', desc: 'Leads arrive at all hours. Your team can\'t be available every second of every day.', color: '#FF2B2B' },
];

export default function ProblemSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="problem" ref={ref} className={`relative py-32 md:py-44 overflow-hidden ${isDark ? 'bg-[#050505]' : 'bg-[#F5F5F7]'}`}>
      {/* Top separator */}
      <div className={`absolute top-0 left-0 right-0 h-px ${isDark ? 'bg-[#1A1A1A]' : 'bg-[#E8E8ED]'}`}></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Apple-style: giant statement first */}
        <div
          className="text-center mb-20 md:mb-28"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}
        >
          <span className="text-[#FF2B2B] text-xs font-bold tracking-widest uppercase block mb-6">The Problem</span>
          <h2
            className={`text-4xl md:text-6xl lg:text-7xl font-black leading-tight mb-8 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}
            style={{ letterSpacing: '-0.03em' }}
          >
            Missed Opportunities<br />
            Are Costing You Revenue.
          </h2>
          <p className={`text-lg md:text-xl max-w-2xl mx-auto leading-relaxed ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`} style={{ fontWeight: 300 }}>
            If your business depends on calls, inquiries, or form submissions,
            delays are expensive. Every delay reduces your chances of conversion.
          </p>
        </div>

        {/* Big stat — Apple style */}
        <div
          className={`mb-20 p-10 md:p-16 rounded-3xl text-center ${isDark ? 'bg-[#0D0D0D] border border-[#1A1A1A]' : 'bg-white border border-[#E8E8ED]'}`}
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.1s' }}
        >
          <div className="text-7xl md:text-9xl font-black text-[#FF2B2B] mb-4" style={{ letterSpacing: '-0.05em' }}>78%</div>
          <p className={`text-lg md:text-2xl font-light max-w-xl mx-auto ${isDark ? 'text-[#C9CED6]/70' : 'text-[#6E6E73]'}`}>
            of customers buy from the <strong className={isDark ? 'text-white font-semibold' : 'text-[#1D1D1F] font-semibold'}>first business that responds</strong> — not the best one.
          </p>
        </div>

        {/* 4-column problem grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {problems.map((p, i) => (
            <div
              key={p.title}
              className={`p-6 rounded-2xl border ${isDark ? 'bg-[#0D0D0D] border-[#1A1A1A] hover:border-[#FF2B2B]/25' : 'bg-white border-[#E8E8ED] hover:border-[#FF2B2B]/30'} transition-all duration-300`}
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: `all 0.7s cubic-bezier(0.16,1,0.3,1) ${0.2 + i * 0.1}s` }}
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#FF2B2B]/10 mb-5">
                <i className={`${p.icon} text-[#FF2B2B] text-lg`}></i>
              </div>
              <h3 className={`font-semibold text-sm leading-snug mb-2 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>{p.title}</h3>
              <p className={`text-xs leading-relaxed ${isDark ? 'text-[#C9CED6]/50' : 'text-[#6E6E73]'}`}>{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
